import { useState, useRef, useEffect } from "react";

interface SearchableDropdownProps {
  id: string;
  label: string;
  placeholder?: string;
  options: { id: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
  disabled?: boolean;
  bg?: string;
}

const SearchableDropdown = ({
  id,
  label,
  options,
  value,
  onChange,
  helperText,
  disabled,
  placeholder,
  bg = "white",
}: SearchableDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the selected option label
  const selectedOption = options.find((option) => option.id === value);
  const displayValue = selectedOption ? selectedOption.label : "";

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Focus the input when opening
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  const handleSelectOption = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="flex flex-col gap-y-2">
      <label
        htmlFor={id}
        className="font-clash-display text-medium-dark-grey text-md font-semibold"
      >
        {label}
      </label>
      <div className={`relative bg-${bg}`} ref={dropdownRef}>
        {/* Display Field */}
        <div
          onClick={handleToggle}
          className={`w-full items-start px-4 pr-10 font-regular text-xs font-clash-display text-medium-dark-grey py-3 border border-stroke rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-button focus:border-transparent cursor-pointer ${disabled
            ? "bg-gray-100 cursor-not-allowed"
            : "bg-white hover:border-primary-button"
            }`}
        >
          <span className={displayValue ? "" : "text-gray-400"}>
            {displayValue || placeholder || "Select an option"}
          </span>
        </div>

        {/* Dropdown Arrow Icon */}
        <div
          className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
          style={{ top: "50%", transform: "translateY(-50%)" }}
        >
          <img
            src="/assets/icons/dropdown-arrow.svg"
            alt="dropdown arrow"
            className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""
              }`}
          />
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-stroke rounded-lg shadow-lg max-h-64 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-stroke">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search..."
                className="w-full px-3 py-2 text-xs font-regular font-clash-display text-medium-dark-grey border border-stroke rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-button focus:border-transparent"
              />
            </div>

            {/* Options List */}
            <div className="overflow-y-auto max-h-48">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => handleSelectOption(option.id)}
                    className={`px-4 py-3 text-xs font-regular font-clash-display cursor-pointer hover:bg-gray-50 transition-colors ${option.id === value
                      ? "bg-primary-button text-white"
                      : "text-medium-dark-grey"
                      }`}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-xs font-regular font-clash-display text-gray-400 text-center">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Helper Text / Error Message */}
      {helperText && <p className="text-red-500 text-md mt-1 font-clash-display">{helperText}</p>}
    </div>
  );
};

export default SearchableDropdown;
